import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { isWeb, useThemeName } from 'tamagui'

import { PaletteBackground } from './PaletteBackground'

import { adaptPalette, parseColorPalette } from './adaptPalette'
import type { AdaptedPalette, RawColorPalette } from './adaptPalette'

type PaletteContextType = {
  palette: AdaptedPalette | null
  raw: RawColorPalette | null
}

type PaletteRootContextType = {
  setPalette: (raw: unknown) => void
}

const PaletteContext = createContext<PaletteContextType>({
  palette: null,
  raw: null,
})

const PaletteRootContext = createContext<PaletteRootContextType>({
  setPalette: () => undefined,
})

export const usePalette = () => useContext(PaletteContext)

/**
 * Root provider — place once in the app shell (e.g. AppShell).
 * Provides the palette context to the entire tree including Footer.
 * Screens set the palette via `usePaletteEffect(rawPalette)`.
 */
export const PaletteRootProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [rawPalette, setRawPalette] = useState<unknown>(null)
  const themeName = useThemeName()
  const mode = themeName.startsWith('dark')
    ? ('dark' as const)
    : ('light' as const)
  const raw = useMemo(() => parseColorPalette(rawPalette), [rawPalette])
  const adapted = useMemo(
    () => (raw ? adaptPalette(raw, mode) : null),
    [raw, mode]
  )

  const value = useMemo(() => ({ palette: adapted, raw }), [adapted, raw])
  const rootValue = useMemo(() => ({ setPalette: setRawPalette }), [])

  return (
    <PaletteRootContext.Provider value={rootValue}>
      <PaletteContext.Provider value={value}>
        {isWeb && adapted && <PaletteBackground palette={adapted} />}
        {children}
      </PaletteContext.Provider>
    </PaletteRootContext.Provider>
  )
}

/**
 * Hook for screens to set the active palette.
 * Clears the palette on unmount so navigating away removes the gradient.
 */
export const usePaletteEffect = (rawPalette: unknown) => {
  const { setPalette } = useContext(PaletteRootContext)

  useEffect(() => {
    setPalette(rawPalette)
    return () => setPalette(null)
  }, [rawPalette, setPalette])
}

/**
 * @deprecated Use PaletteRootProvider at app shell + usePaletteEffect in screens.
 * Kept for backwards compatibility during migration.
 */
export const PaletteProvider = ({
  palette: rawPalette,
  children,
}: {
  palette: unknown
  children: React.ReactNode
}) => {
  usePaletteEffect(rawPalette)
  return <>{children}</>
}
